import {
  useBuildPermissions,
  useCurrentUserResource,
  useGetUserAuthenticated,
  usePermissions,
} from '@dispatch/shared/hooks';
import { User } from '@dispatch/shared/types';
import {
  RequireAuth,
  useAccessToken,
  useConfigureAuth,
  useCurrentUser,
} from '@dispatch/web/auth';
import { useSyncTelemetryUser } from '@dispatch/web/telemetry';
import { AppLayout, routes } from '@dispatch/web/ui';
import { Container, Grid } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { Outlet } from 'react-router-dom';

export function App() {
  const { isAccessTokenSet } = useAccessToken();
  const { fullName, userId, tenantId } = useCurrentUser();
  const { userPermissions, status: permissionStatus } = useBuildPermissions(
    userId || '',
    isAccessTokenSet
  );
  const { permissions, setPermissions } = usePermissions();
  const { data, status: userStatus } = useGetUserAuthenticated(
    userId || '',
    isAccessTokenSet
  );
  const user: User | undefined = data?.data?.[0];

  const { resourceId, setResourceId, setIsAdjuster } = useCurrentUserResource();

  useEffect(() => {
    if (!permissions && userPermissions) {
      setPermissions(userPermissions);
    }
  }, [userPermissions, permissions, setPermissions]);

  useEffect(() => {
    if (!resourceId && user?.resourceId) {
      setResourceId(user?.resourceId);
      setIsAdjuster(true);
    }
  }, [resourceId, setIsAdjuster, setResourceId, user?.resourceId]);

  const status = useMemo(() => {
    if (permissionStatus === 'loading' || userStatus === 'loading') {
      return 'loading';
    }
    if (permissionStatus === 'success' && userStatus === 'success') {
      return 'success';
    }

    return undefined;
  }, [permissionStatus, userStatus]);

  useSyncTelemetryUser(userId, tenantId);
  useConfigureAuth();

  return (
    <RequireAuth>
      <AppLayout
        routes={routes}
        brandName="Dispatch"
        currentUserName={fullName || ''}
        status={status}
        permissions={permissions}
        user={user}
      >
        <Container maxWidth={false} sx={{ mb: 1, padding: '0 !important' }}>
          <Grid container padding={0}>
            <Outlet />
            {/* Start rendering router matches */}
          </Grid>
        </Container>
      </AppLayout>
    </RequireAuth>
  );
}

export default App;
